import { PrototypeApiError } from './apiClient';
import { byId, inputValue } from './domUtils';
import { closeModal, openModal } from './navigationController';
import { notifySuccess } from './prototypeRuntimeSupport';
import type { PrototypeState } from './controllerState';
import type { PrototypeRuntime, Root } from './prototypeTypes';
import { validatePassword, validatePasswordMatch } from './validation';
import { validateCurrentPassword } from '../../domain/passwordPolicy';
import { setAuthButtonLoading } from './actions/authButtonLock';
import { resetSetPasswordTurnstileAfterError } from './turnstileWidgetController';

export async function handleProfilePasswordAction(root: Root, state: PrototypeState): Promise<void> {
	const button = byId(root, 'profilePasswordAction') as HTMLButtonElement | null;
	if (button?.dataset.hasPassword === '1') {
		paintFeedback(root, null);
		openModal(root, 'changePasswordModal');
		return;
	}
	// Google-only user: open confirm modal with Turnstile instead of sending immediately
	paintSetPasswordFeedback(root, null);
	openModal(root, 'setPasswordConfirmModal');
}

export async function submitSetPasswordConfirm(root: Root, state: PrototypeState): Promise<void> {
	const button = byId(root, 'setPasswordConfirmBtn') as HTMLButtonElement | null;
	const originalText = button?.textContent ?? 'Send setup link';
	setAuthButtonLoading(button, true, originalText, 'Sending…');
	paintSetPasswordFeedback(root, null);
	try {
		const turnstileToken = state.runtime.interactiveTurnstile?.hasToken()
			? await state.runtime.interactiveTurnstile.getToken('auth-set-password')
			: null;
		if (!turnstileToken) {
			paintSetPasswordFeedback(root, 'Please complete the verification to continue.');
			return;
		}
		await state.runtime.api.requestPasswordSetup(turnstileToken);
		closeModal(root, 'setPasswordConfirmModal');
		notifySuccess('Check your email for a secure link to set your password.');
	} catch (error) {
		if (error instanceof PrototypeApiError && (error.status === 403)) {
			resetSetPasswordTurnstileAfterError(root, state.runtime);
			paintSetPasswordFeedback(root, 'Verification failed. Please retry the challenge.');
		} else if (error instanceof PrototypeApiError && error.status === 429) {
			paintSetPasswordFeedback(root, 'Too many password setup requests. Please wait a minute and try again.');
		} else {
			paintSetPasswordFeedback(root, 'Could not send the password setup email. Please try again.');
		}
	} finally {
		setAuthButtonLoading(button, false, originalText);
	}
}

export function closeSetPasswordConfirmModal(root: Root, runtime: PrototypeRuntime): void {
	void runtime;
	closeModal(root, 'setPasswordConfirmModal');
}

export async function submitProfilePasswordChange(root: Root, state: PrototypeState): Promise<void> {
	const currentPassword = inputValue(root, 'profileCurrentPassword');
	const newPassword = inputValue(root, 'profileNewPassword');
	const confirmPassword = inputValue(root, 'profileConfirmPassword');
	const error = validateCurrentPassword(currentPassword)
		?? validatePassword(newPassword) ?? validatePasswordMatch(newPassword, confirmPassword);
	if (error) { paintFeedback(root, error); return; }
	const button = byId(root, 'profilePasswordSubmit') as HTMLButtonElement | null;
	setAuthButtonLoading(button, true, 'Change password', 'Changing…');
	paintFeedback(root, null);
	try {
		await state.runtime.api.changePassword({ currentPassword, newPassword });
		clearPasswordForm(root);
		closeModal(root, 'changePasswordModal');
		notifySuccess('Password changed. Other signed-in devices were signed out.');
	} catch (error) {
		paintFeedback(root, error instanceof PrototypeApiError && error.status === 401
			? 'Your current password is incorrect.'
			: error instanceof PrototypeApiError && error.status === 429
				? 'Too many password change attempts. Please wait a minute and try again.'
				: 'Could not change your password. Please try again.');
	} finally {
		setAuthButtonLoading(button, false, 'Change password');
	}
}

export function closeProfilePasswordModal(root: Root): void {
	clearPasswordForm(root);
	paintFeedback(root, null);
	closeModal(root, 'changePasswordModal');
}

function clearPasswordForm(root: Root): void {
	for (const id of ['profileCurrentPassword', 'profileNewPassword', 'profileConfirmPassword']) {
		const input = byId(root, id) as HTMLInputElement | null;
		if (input) input.value = '';
	}
}

function paintFeedback(root: Root, message: string | null, success = false): void {
	const node = byId(root, 'profilePasswordFeedback');
	if (!node) return;
	node.textContent = message ?? '';
	node.hidden = !message;
	node.style.color = success ? 'var(--success)' : '';
}

function paintSetPasswordFeedback(root: Root, message: string | null): void {
	const node = byId(root, 'setPasswordConfirmFeedback');
	if (!node) return;
	node.textContent = message ?? '';
	node.hidden = !message;
}
